import * as React from 'react';
import { Link } from 'react-router';
import { push } from 'react-router-redux';
import styled from 'styled-components';
import { rem } from 'polished';
import { __ } from '@bart.sk-eshop/react-i18n/lib';

import API, { ThenArg } from '../../../../services/API';
import { URL_LOGIN } from '../../../../constants';
import Searchform from '../Searchform';
import {
  isCartOnlyDemand,
  formatPrice,
  showPriceWithVat,
} from '../../../../utilities';
import {
  AccountIcon,
  BurgerIcon,
  CartIcon,
  HeartIcon,
} from '../HeaderNew/icons';
import {
  AUDIENCE_URLS,
  AudienceKey,
  homeUrlForCurrentTab,
  MenuNode,
} from '../HeaderNew/menuData';
import MobileMenuDrawer, { DrawerView } from './MobileMenuDrawer';

const LOGO = '/images/logo.svg';

interface HeaderMobileProps {
  mainMenu: MenuNode[];
  user: any;
  cart: ThenArg<typeof API.getCart>;
  cartError?: any;
  deliveryFreePrice: number;
  settings: any;
  dispatch: any;
  currentPath: string;
  currentUniqs: string[];
  currentCategoryGroupId?: number;
  currentCategoryId?: number;
  categoriesGroup?: any[];
  favoriteProductsCount: number;
  queryAudience?: AudienceKey | null;
  searchDropdownVisible: boolean;
  searchedQuery: string;
  searchedResults: any[];
  searchedResultsLoader: boolean;
  onChangeSearchInput: (q: string) => void;
  onClickOutsideSearchForm: () => void;
  onRedirect: (url) => void;
  onSubmitSearchInput: (section: string) => void;
  onAppendCategories: () => void;
  onAppendProducts: () => void;
  onAppendWebContents: () => void;
  onSearchFocus: (isFocus: boolean) => void;
}

function HeaderMobile(props: HeaderMobileProps) {
  const {
    mainMenu,
    user,
    cart,
    settings,
    dispatch,
    currentPath,
    currentUniqs,
    categoriesGroup,
    favoriteProductsCount,
    queryAudience,
  } = props;

  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [drawerView, setDrawerView] = React.useState<DrawerView>('menu');

  const [homeUrl, setHomeUrl] = React.useState(() =>
    queryAudience
      ? AUDIENCE_URLS[queryAudience]
      : homeUrlForCurrentTab(mainMenu, currentPath, currentUniqs, false),
  );
  React.useEffect(() => {
    setHomeUrl(
      queryAudience
        ? AUDIENCE_URLS[queryAudience]
        : homeUrlForCurrentTab(mainMenu, currentPath, currentUniqs),
    );
  }, [mainMenu, currentPath, currentUniqs, queryAudience]);

  const openDrawer = (view: DrawerView) => {
    setDrawerView(view);
    setDrawerOpen(true);
  };

  React.useEffect(() => {
    setDrawerOpen(false);
  }, [currentPath]);

  const isOnlyDemand = isCartOnlyDemand(cart);
  const cartCount = cart && cart.items ? cart.items.length : 0;
  const showWithVat = showPriceWithVat(user);
  let cartTotalPrice = 0;
  if (cart && cart.total_items_price_with_discount && showWithVat) {
    cartTotalPrice = cart.total_items_price_with_discount;
  } else if (cart && cart.total_items_price_without_vat) {
    cartTotalPrice =
      cart.customer_card_discount && cart.customer_card_discount > 0
        ? cart.total_items_price_with_discount_without_vat || 0
        : cart.total_items_price_without_vat;
  }
  const showCartTotal = cartCount > 0 && !isOnlyDemand;

  const onClickFavorite = () => dispatch(push('/my-account/favorite-products'));
  const onClickAccount = () =>
    user ? openDrawer('account') : dispatch(push(URL_LOGIN));
  const onClickCart = () => dispatch(push('/kosik'));

  return (
    <>
      <TopRow>
        <LogoLink to={homeUrl}>
          <LogoImg src={LOGO} alt="Daffer.sk" />
        </LogoLink>
        <Actions>
          {user ? (
            <Action type="button" onClick={onClickFavorite}>
              <IconWrap>
                <HeartIcon className="ico" />
              </IconWrap>
              <ActionLabel>
                {favoriteProductsCount ? favoriteProductsCount : __('Obľúb.')}
              </ActionLabel>
            </Action>
          ) : null}
          <Action type="button" onClick={onClickAccount}>
            <IconWrap>
              <AccountIcon className="ico account" />
            </IconWrap>
            <ActionLabel>{__('Účet')}</ActionLabel>
          </Action>
          <Action type="button" onClick={onClickCart}>
            <IconWrap>
              <CartIcon className="ico" />
              {cartCount && !showCartTotal ? <Badge>{cartCount}</Badge> : null}
            </IconWrap>
            {showCartTotal ? (
              <PricePill>{formatPrice(cartTotalPrice, 'EUR')}</PricePill>
            ) : (
              <ActionLabel>{__('Košík')}</ActionLabel>
            )}
          </Action>
          <Action type="button" onClick={() => openDrawer('menu')}>
            <IconWrap>
              <BurgerIcon className="ico" />
            </IconWrap>
            <ActionLabel>{__('Menu')}</ActionLabel>
          </Action>
        </Actions>
      </TopRow>
      <SearchRow>
        <Searchform
          searchDropdownVisible={props.searchDropdownVisible}
          searchedQuery={props.searchedQuery}
          searchedResults={props.searchedResults}
          searchedResultsLoader={props.searchedResultsLoader}
          onChangeSearchInput={props.onChangeSearchInput}
          onSubmitSearchInput={props.onSubmitSearchInput}
          onAppendCategories={props.onAppendCategories}
          onAppendProducts={props.onAppendProducts}
          onAppendWebContents={props.onAppendWebContents}
          onClickOutsideSearchForm={props.onClickOutsideSearchForm}
          onRedirect={props.onRedirect}
          onSearchFocus={props.onSearchFocus}
          isMobile={true}
          user={user}
          cart={cart}
          settings={settings}
        />
      </SearchRow>

      <MobileMenuDrawer
        open={drawerOpen}
        view={drawerView}
        onClose={() => setDrawerOpen(false)}
        mainMenu={mainMenu}
        categoriesGroup={categoriesGroup}
        user={user}
        dispatch={dispatch}
        currentPath={currentPath}
        currentUniqs={currentUniqs}
        currentCategoryGroupId={props.currentCategoryGroupId}
        currentCategoryId={props.currentCategoryId}
        queryAudience={queryAudience}
      />
    </>
  );
}

const TopRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(8)};
  height: ${rem(64)};
  /* Figma "Frame 181": 360px bar, 16px side padding. */
  padding: 0 ${rem(16)};
`;

const LogoLink = styled(Link)`
  flex: 0 0 auto;
  display: block;
  /* Figma logo: 130×40. */
  width: ${rem(130)};
`;

const LogoImg = styled.img`
  display: block;
  width: 100%;
  height: auto;
`;

const Actions = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: ${rem(8)};
`;

const Action = styled.button`
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  /* Figma action box: 40px wide, icon + 11px label stacked. */
  min-width: ${rem(40)};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(4)};
  color: ${({ theme }) => theme.color.white};
  font-family: inherit;

  .ico {
    width: ${rem(24)};
    height: ${rem(24)};
  }
  .ico.account {
    /* AccountIcon is authored on a 32px viewBox; render it at the same 24px. */
    width: ${rem(24)};
    height: ${rem(24)};
  }
`;

const IconWrap = styled.span`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${rem(26)};
`;

const ActionLabel = styled.span`
  font-size: ${rem(11)};
  line-height: 1;
  font-weight: 600;
  white-space: nowrap;
  color: ${({ theme }) => theme.color.white};
`;

const PricePill = styled.span`
  background: ${({ theme }) => theme.color.secondary};
  color: #171717;
  font-size: ${rem(12)};
  font-weight: 600;
  line-height: 1;
  border-radius: ${rem(999)};
  padding: ${rem(3)} ${rem(4)};
  white-space: nowrap;
`;

const Badge = styled.span`
  position: absolute;
  top: ${rem(-6)};
  right: ${rem(-8)};
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: ${rem(16)};
  height: ${rem(16)};
  padding: 0 ${rem(4)};
  border-radius: ${rem(999)};
  background: ${({ theme }) => theme.color.secondary};
  color: #171717;
  font-size: ${rem(10)};
  font-weight: 700;
  line-height: 1;
`;

const SearchRow = styled.div`
  width: 100%;
`;

export default HeaderMobile;
